import { gql } from '@apollo/client';

// Account mutations
export const CREATE_ACCOUNT = gql`
  mutation CreateAccount($input: CreateAccountInput!) {
    createAccount(input: $input) {
      id
      name
      createdAt
    }
  }
`;

// WordPress connect: approve a site → mint a short-lived pairing code the
// plugin exchanges (server-to-server) for a durable connection token.
export const CREATE_WP_CONNECTION = gql`
  mutation CreateWpConnection($accountId: ID!, $siteUrl: String!, $siteName: String) {
    createWpConnection(accountId: $accountId, siteUrl: $siteUrl, siteName: $siteName) {
      code
      expiresAt
      accountId
      dataSourceId
    }
  }
`;

export const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $input: UpdateAccountInput!) {
    updateAccount(id: $id, input: $input) {
      id
      name
      isActive
      updatedAt
      urlShortening
      bitlyToken
      firstCommentPrefix
      utmCampaign
      timezone
    }
  }
`;

export const ADD_PAYMENT_METHOD = gql`
  mutation AddPaymentMethod($accountId: ID!, $paymentMethod: String!) {
    updatePaymentMethod(accountId: $accountId, paymentMethod: $paymentMethod) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
    }
  }
`;

export const MARK_PAYMENT_METHOD_DEFAULT = gql`
  mutation MarkPaymentMethodDefault($accountId: ID!, $paymentMethod: String!) {
    setDefaultPaymentMethod(accountId: $accountId, paymentMethod: $paymentMethod) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
    }
  }
`;
export const REMOVE_PAYMENT_METHOD = gql`
  mutation RemovePaymentMethod($accountId: ID!, $paymentMethod: String!) {
    deletePaymentMethod(accountId: $accountId, paymentMethod: $paymentMethod) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
    }
  }
`;

export const UPDATE_PRODUCT_PLAN = gql`
  mutation UpdateProductPlan(
    $accountId: ID!
    $productPlan: String!
    $billingInterval: String
    $product: String
  ) {
    updateProductPlan(
      accountId: $accountId
      plan: $productPlan
      billingInterval: $billingInterval
      product: $product
    ) {
      id
      name
      createdAt
      plan
      isActive
      techContactEmail
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($accountId: ID!) {
    cancelSubscription(accountId: $accountId) {
      id
      name
      createdAt
      plan
      isActive
      techContactEmail
    }
  }
`;
export const ADD_USER_TO_ACCOUNT = gql`
  mutation AddUserToAccount($input: AddUserToAccountInput!) {
    addUserToAccount(input: $input) {
      user {
        id
        email
      }
      role
      createdAt
    }
  }
`;
export const UPDATE_USER_ROLE = gql`
  mutation UpdateUserRole($accountId: ID!, $id: ID!, $role: String!) {
    updateUserAccount(accountId: $accountId, id: $id, role: $role) {
      id
      role
      accountId
      userId
    }
  }
`;
export const REMOVE_USER_FROM_ACCOUNT = gql`
  mutation RemoveUserFromAccount($accountId: ID!, $id: ID!) {
    removeUserFromAccount(accountId: $accountId, id: $id) {
      id
      role
      accountId
      userId
    }
  }
`;
// Social profile mutations
export const CREATE_SOCIAL_PROFILE = gql`
  mutation CreateSocialProfile($input: CreateSocialProfileInput!) {
    createSocialProfile(input: $input) {
      id
      platform
      profileName
      profileId
      createdAt
      isActive
    }
  }
`;

export const UPDATE_SOCIAL_PROFILE = gql`
  mutation UpdateSocialProfile($id: ID!, $input: UpdateSocialProfileInput!) {
    updateSocialProfile(id: $id, input: $input) {
      id
      platform
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
    }
  }
`;

export const CREATE_DATA_SOURCE = gql`
  mutation CreateDataSource($input: CreateDataSourceInput!) {
    createDataSource(input: $input) {
      id
      name
      type
      url
      createdAt
      isActive
    }
  }
`;

export const UPDATE_DATA_SOURCE = gql`
  mutation UpdateDataSource($id: ID!, $input: UpdateDataSourceInput!) {
    updateDataSource(id: $id, input: $input) {
      id
      name
      type
      url
      isActive
      updatedAt
    }
  }
`;

// Claude Cowork MCP — plaintext clientId/clientSecret returned ONCE.
export const CREATE_COWORK_MCP_CONNECTOR = gql`
  mutation CreateCoworkMcpConnector($accountId: ID!, $label: String) {
    createCoworkMcpConnector(accountId: $accountId, label: $label) {
      mcpUrl
      clientId
      clientSecret
      tokenPrefix
      dataSourceId
      tokenId
    }
  }
`;

export const ROTATE_COWORK_MCP_CREDENTIALS = gql`
  mutation RotateCoworkMcpCredentials($accountId: ID!, $label: String) {
    rotateCoworkMcpCredentials(accountId: $accountId, label: $label) {
      mcpUrl
      clientId
      clientSecret
      tokenPrefix
      dataSourceId
      tokenId
    }
  }
`;

// Atomization config mutations (replace-all)
export const SET_DATA_SOURCE_CUSTOM_FIELDS = gql`
  mutation SetDataSourceCustomFields($dataSourceId: ID!, $fields: [DataSourceCustomFieldInput!]!) {
    setDataSourceCustomFields(dataSourceId: $dataSourceId, fields: $fields) {
      id
      dataSourceId
      fieldName
      sourceKind
      locator
      isActive
      taxonomyKey
      valueDelimiter
    }
  }
`;

export const SET_ACCOUNT_TAXONOMY = gql`
  mutation SetAccountTaxonomy($accountId: ID!, $dimensions: [TaxonomyDimensionInput!]!) {
    setAccountTaxonomy(accountId: $accountId, dimensions: $dimensions) {
      key
      terms
      neverDerive
      isFreeform
    }
  }
`;

export const SET_ACCOUNT_TOPIC_FIELDS = gql`
  mutation SetAccountTopicFields($accountId: ID!, $fields: [AccountTopicFieldInput!]!) {
    setAccountTopicFields(accountId: $accountId, fields: $fields) {
      id
      accountId
      fieldKey
      label
      description
      fieldType
      required
      options
      uiGroup
      sortOrder
      isActive
    }
  }
`;

// Content review mutations
export const UPDATE_TOPIC = gql`
  mutation UpdateTopic($id: ID!, $input: UpdateTopicInput!) {
    updateTopic(id: $id, input: $input) {
      id
      title
      summary
      mediaUrl
      destinationUrl
      keywords
      publishedDate
      category
    }
  }
`;

export const UPDATE_TOPIC_TAXONOMY = gql`
  mutation UpdateTopicTaxonomy($topicId: ID!, $taxonomies: [TaxonomyValueInput!]!) {
    updateTopicTaxonomy(topicId: $topicId, taxonomies: $taxonomies) {
      id
      taxonomies
    }
  }
`;

export const UPDATE_TOPIC_FACTS = gql`
  mutation UpdateTopicFacts($topicId: ID!, $facts: [VerifiedFactInput!]!) {
    updateTopicFacts(topicId: $topicId, facts: $facts) {
      id
      verifiedFacts
    }
  }
`;

export const UPDATE_TOPIC_ENTITIES = gql`
  mutation UpdateTopicEntities(
    $topicId: ID!
    $keyPeople: [NamedEntityInput!]!
    $keyOrganizations: [NamedEntityInput!]!
    $keyLocations: [NamedEntityInput!]!
  ) {
    updateTopicEntities(
      topicId: $topicId
      keyPeople: $keyPeople
      keyOrganizations: $keyOrganizations
      keyLocations: $keyLocations
    ) {
      id
      keyPeople
      keyOrganizations
      keyLocations
    }
  }
`;

export const SET_CONTENT_TYPE = gql`
  mutation SetContentType($topicId: ID!, $contentType: String!) {
    setContentType(topicId: $topicId, contentType: $contentType) {
      id
      contentType
    }
  }
`;

export const SAVE_CONTENT_ITEM = gql`
  mutation SaveContentItem(
    $id: ID!
    $input: SaveContentItemInput!
    $createVersion: Boolean = true
  ) {
    saveContentItem(id: $id, input: $input, createVersion: $createVersion) {
      id
      title
      summary
      bodyText
      mediaUrl
      destinationUrl
      keywords
      publishedDate
      category
      contentType
      publishStatus
      taxonomies
      customFields
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
    }
  }
`;

export const RESTORE_TOPIC_VERSION = gql`
  mutation RestoreTopicVersion($topicId: ID!, $versionId: ID!) {
    restoreTopicVersion(topicId: $topicId, versionId: $versionId) {
      id
      title
      summary
      bodyText
      mediaUrl
      destinationUrl
      keywords
      publishedDate
      category
      contentType
      publishStatus
      taxonomies
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
    }
  }
`;

export const PUBLISH_TOPIC = gql`
  mutation PublishTopic($id: ID!) {
    publishTopic(id: $id) {
      id
      publishStatus
      publishedDate
    }
  }
`;

export const SCHEDULE_TOPIC = gql`
  mutation ScheduleTopic($id: ID!, $publishedDate: String!) {
    scheduleTopic(id: $id, publishedDate: $publishedDate) {
      id
      publishStatus
      publishedDate
    }
  }
`;

export const UNPUBLISH_TOPIC = gql`
  mutation UnpublishTopic($id: ID!) {
    unpublishTopic(id: $id) {
      id
      publishStatus
      publishedDate
    }
  }
`;

export const DELETE_TOPIC = gql`
  mutation DeleteTopic($id: ID!) {
    deleteTopic(id: $id) {
      id
      isActive
    }
  }
`;

export const ATOMIZE_TOPIC = gql`
  mutation AtomizeTopic($topicId: ID!) {
    atomizeTopic(topicId: $topicId) {
      id
      taxonomies
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
      atomizedAt
    }
  }
`;

export const SET_DATA_SOURCE_TAXONOMY_SUBSET = gql`
  mutation SetDataSourceTaxonomySubset(
    $dataSourceId: ID!
    $dimensions: [TaxonomyDimensionInput!]!
  ) {
    setDataSourceTaxonomySubset(dataSourceId: $dataSourceId, dimensions: $dimensions) {
      key
      terms
    }
  }
`;

export const CREATE_AUTOMATION_RULE = gql`
  mutation CreateAutomationRule($input: CreateAutomationRuleInput!) {
    createAutomationRule(input: $input) {
      id
      name
      type
      socialProfileId
      priority
      postTypeStrategy
      dbFilterRule
      createdAt
      isActive
    }
  }
`;

export const UPDATE_AUTOMATION_RULE = gql`
  mutation UpdateAutomationRule($id: ID!, $input: UpdateAutomationRuleInput!) {
    updateAutomationRule(id: $id, input: $input) {
      id
      name
      type
      socialProfileId
      priority
      postTypeStrategy
      dbFilterRule
      createdAt
      isActive
    }
  }
`;

export const DELETE_AUTOMATION_RULE = gql`
  mutation DeleteAutomationRule($id: ID!) {
    deleteAutomationRule(id: $id) {
      id
      name
    }
  }
`;

// Posting Content Age mutations
export const CREATE_POSTING_CONTENT_AGE = gql`
  mutation CreatePostingContentAge($input: CreatePostingContentAgeInput!) {
    createPostingContentAge(input: $input) {
      id
      postingRuleId
      minAgeHours
      maxAgeHours
      isImmediate
      delayOffsetHours
      isActive
    }
  }
`;

export const UPDATE_POSTING_CONTENT_AGE = gql`
  mutation UpdatePostingContentAge($id: ID!, $input: UpdatePostingContentAgeInput!) {
    updatePostingContentAge(id: $id, input: $input) {
      id
      postingRuleId
      minAgeHours
      maxAgeHours
      isImmediate
      delayOffsetHours
      isActive
    }
  }
`;

export const DELETE_POSTING_CONTENT_AGE = gql`
  mutation DeletePostingContentAge($id: ID!) {
    deletePostingContentAge(id: $id) {
      id
      postingRuleId
    }
  }
`;

// Posting Schedule Preference mutations
export const CREATE_POSTING_SCHEDULE_PREFERENCE = gql`
  mutation CreatePostingSchedulePreference($input: CreatePostingSchedulePreferenceInput!) {
    createPostingSchedulePreference(input: $input) {
      id
      postingRuleId
      minuteMark
      intervalMinutes
      isActive
    }
  }
`;

export const UPDATE_POSTING_SCHEDULE_PREFERENCE = gql`
  mutation UpdatePostingSchedulePreference(
    $id: ID!
    $input: UpdatePostingSchedulePreferenceInput!
  ) {
    updatePostingSchedulePreference(id: $id, input: $input) {
      id
      postingRuleId
      minuteMark
      intervalMinutes
      isActive
    }
  }
`;

export const DELETE_POSTING_SCHEDULE_PREFERENCE = gql`
  mutation DeletePostingSchedulePreference($id: ID!) {
    deletePostingSchedulePreference(id: $id) {
      id
      postingRuleId
    }
  }
`;

// Posting Time Window mutations
export const CREATE_POSTING_TIME_WINDOW = gql`
  mutation CreatePostingTimeWindow($input: CreatePostingTimeWindowInput!) {
    createPostingTimeWindow(input: $input) {
      id
      postingRuleId
      dayOfWeek
      startTime
      endTime
      spansMidnight
      isActive
    }
  }
`;

export const UPDATE_POSTING_TIME_WINDOW = gql`
  mutation UpdatePostingTimeWindow($id: ID!, $input: UpdatePostingTimeWindowInput!) {
    updatePostingTimeWindow(id: $id, input: $input) {
      id
      postingRuleId
      dayOfWeek
      startTime
      endTime
      spansMidnight
      isActive
    }
  }
`;

export const CREATE_POST_MUTATION = gql`
  mutation CreateSocialPost($input: CreateSocialPostInput!) {
    createSocialPost(input: $input) {
      id
      title
      summary
      postUrl
      imageUrl
      thoughts
      scheduleTime
      postType
      platform
      socialProfileId
      accountId
      notes
      isDraft
    }
  }
`;

export const UPDATE_POST_MUTATION = gql`
  mutation UpdateSocialPost($id: ID!, $input: UpdateSocialPostInput!) {
    updateSocialPost(id: $id, input: $input) {
      id
      title
      summary
      postUrl
      imageUrl
      postType
      thoughts
      scheduleTime
      notes
      isDraft
    }
  }
`;
export const DELETE_POSTING_TIME_WINDOW = gql`
  mutation DeletePostingTimeWindow($id: ID!) {
    deletePostingTimeWindow(id: $id) {
      id
      postingRuleId
    }
  }
`;

export const REQUEST_POST_DELETION = gql`
  mutation RequestPostDeletion($id: ID!) {
    requestPostDeletion(id: $id) {
      id
      deletionStatus
      updatedAt
    }
  }
`;

export const UNDO_POST_DELETION = gql`
  mutation UndoPostDeletion($id: ID!) {
    undoPostDeletion(id: $id) {
      id
      deletionStatus
      updatedAt
    }
  }
`;

export const CREATE_TOPIC_MUTATION = gql`
  mutation CreateTopic($input: CreateTopicInput!) {
    createTopic(input: $input) {
      id
      title
      summary
      mediaType
      mediaUrl
      destinationUrl
      keywords
      category
      publishedDate
      contentType
      bodyText
      publishStatus
    }
  }
`;

export const CREATE_SETUP_INTENT = gql`
  mutation SetupIntent {
    setupIntent {
      id
      clientSecret
      status
    }
  }
`;

export const SIGNUP_MUTATION = gql`
  mutation Signup(
    $email: String!
    $password: String!
    $accountName: String!
    $paymentName: String
    $paymentMethod: String
    $plan: String!
    $products: [String!]
    $billingInterval: String
  ) {
    signup(
      email: $email
      password: $password
      accountName: $accountName
      paymentName: $paymentName
      paymentMethod: $paymentMethod
      plan: $plan
      products: $products
      billingInterval: $billingInterval
    )
  }
`;

// Project mutations
export const CREATE_PROJECT = gql`
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
      accountId
      name
      plan
      deadline
      status
      progress
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($id: ID!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      id
      accountId
      name
      plan
      deadline
      status
      progress
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      notes
      updatedAt
    }
  }
`;

export const DELETE_PROJECT = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id) {
      id
      name
    }
  }
`;

// Project Team Member mutations
export const ADD_PROJECT_TEAM_MEMBER = gql`
  mutation AddProjectTeamMember($input: AddProjectTeamMemberInput!) {
    addProjectTeamMember(input: $input) {
      id
      projectId
      userId
      role
      user {
        id
        email
      }
    }
  }
`;

export const REMOVE_PROJECT_TEAM_MEMBER = gql`
  mutation RemoveProjectTeamMember($id: ID!) {
    removeProjectTeamMember(id: $id) {
      id
    }
  }
`;

export const UPDATE_PROJECT_TEAM_MEMBER = gql`
  mutation UpdateProjectTeamMember($id: ID!, $role: String!) {
    updateProjectTeamMember(id: $id, role: $role) {
      id
      role
    }
  }
`;

// Project Post mutations
export const CREATE_PROJECT_POST = gql`
  mutation CreateProjectPost($input: CreateProjectPostInput!) {
    createProjectPost(input: $input) {
      id
      projectId
      accountId
      title
      content
      status
      assignedTo
      scheduledDate
      dueDate
      postType
      notes
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PROJECT_POST = gql`
  mutation UpdateProjectPost($id: ID!, $input: UpdateProjectPostInput!) {
    updateProjectPost(id: $id, input: $input) {
      id
      title
      content
      status
      assignedTo
      scheduledDate
      dueDate
      postType
      topicId
      notes
      updatedAt
    }
  }
`;

export const DELETE_PROJECT_POST = gql`
  mutation DeleteProjectPost($id: ID!) {
    deleteProjectPost(id: $id) {
      id
      title
    }
  }
`;

export const PUBLISH_PROJECT_POST_TO_SOCIAL = gql`
  mutation PublishProjectPostToSocial($projectPostId: ID!) {
    publishProjectPostToSocial(projectPostId: $projectPostId) {
      success
      message
      socialPosts {
        id
        title
        platform
        socialProfileId
        accountId
      }
    }
  }
`;

// Project Post Target mutations
export const ADD_PROJECT_POST_TARGET = gql`
  mutation AddProjectPostTarget($input: AddProjectPostTargetInput!) {
    addProjectPostTarget(input: $input) {
      id
      projectPostId
      socialProfileId
      platform
      socialProfile {
        id
        profileName
        platform
      }
    }
  }
`;

export const REMOVE_PROJECT_POST_TARGET = gql`
  mutation RemoveProjectPostTarget($id: ID!) {
    removeProjectPostTarget(id: $id) {
      id
    }
  }
`;

// Research mutations
export const UPSERT_RESEARCH_SETTINGS = gql`
  mutation UpsertResearchSettings($accountId: ID!, $input: UpsertResearchSettingsInput!) {
    upsertResearchSettings(accountId: $accountId, input: $input) {
      id
      accountId
      searchEndpoint
      openaiProvider
      openaiEndpoint
      openaiApiKey
      openaiModel
      isActive
      updatedAt
    }
  }
`;

export const ASSEMBLE_ARTICLE = gql`
  mutation AssembleArticle($accountId: ID!, $input: AssembleArticleInput!) {
    assembleArticle(accountId: $accountId, input: $input) {
      suggestedTitle
      suggestedExcerpt
      sourceWarning
      blocks
      sourceArticles
      selectionInfo
      factIndex
      dedupStats
      verification
      expectationSummary
    }
  }
`;

export const PUBLISH_ASSEMBLED_ARTICLE = gql`
  mutation PublishAssembledArticle($accountId: ID!, $input: PublishAssembledArticleInput!) {
    publishAssembledArticle(accountId: $accountId, input: $input) {
      id
      title
      publishStatus
    }
  }
`;

export const RECORD_RESEARCH_FEEDBACK = gql`
  mutation RecordResearchFeedback($accountId: ID!, $queryId: ID!, $feedback: String!) {
    recordResearchFeedback(accountId: $accountId, queryId: $queryId, feedback: $feedback)
  }
`;

export const SUMMARIZE_CONTENT_DRAFT = gql`
  mutation SummarizeContentDraft($accountId: ID!, $title: String, $bodyText: String!) {
    summarizeContentDraft(accountId: $accountId, title: $title, bodyText: $bodyText) {
      bullets
      factCount
    }
  }
`;

// ── Media asset management ──────────────────────────────────────────────────
// NOTE: the two upload mutations below are NO LONGER USED BY THIS APP. Browser
// uploads go through POST /api/upload/asset (see lib/media/uploadAsset) so that
// no page script ever holds a write-capable storage URL, and so uploading does
// not depend on the storage account's CORS rules. They are kept because the
// direct-to-blob path is still the right one for machine clients — the studio
// capture app talks to it server-to-server — and because a document that has
// drifted from its schema is worse than one that is merely unused.

export const CREATE_MEDIA_UPLOAD = gql`
  mutation CreateMediaUpload($accountId: ID!, $input: CreateMediaUploadInput!) {
    createMediaUpload(accountId: $accountId, input: $input) {
      deduped
      asset {
        id
        sha256
        storageKey
        mime
      }
      upload {
        uploadUrl
        method
        headers
        expiresAt
      }
    }
  }
`;

export const FINALIZE_MEDIA_UPLOAD = gql`
  mutation FinalizeMediaUpload($accountId: ID!, $assetId: ID!) {
    finalizeMediaUpload(accountId: $accountId, assetId: $assetId) {
      id
      bytes
      mime
    }
  }
`;

export const RESTORE_MEDIA_RENDITION = gql`
  mutation RestoreMediaRendition($accountId: ID!, $renditionId: ID!) {
    restoreMediaRendition(accountId: $accountId, renditionId: $renditionId) {
      id
      kind
      isCurrent
      url
    }
  }
`;

export const DELETE_MEDIA_ASSET = gql`
  mutation DeleteMediaAsset($accountId: ID!, $assetId: ID!) {
    deleteMediaAsset(accountId: $accountId, assetId: $assetId)
  }
`;

export const LINK_MEDIA = gql`
  mutation LinkMedia($accountId: ID!, $input: LinkMediaInput!) {
    linkMedia(accountId: $accountId, input: $input)
  }
`;

export const UNLINK_MEDIA = gql`
  mutation UnlinkMedia(
    $accountId: ID!
    $ownerType: String!
    $ownerId: ID!
    $role: String!
    $position: Int
  ) {
    unlinkMedia(
      accountId: $accountId
      ownerType: $ownerType
      ownerId: $ownerId
      role: $role
      position: $position
    )
  }
`;

/**
 * Public site identity. Claiming a slug used to require direct SQL; the server
 * validates the format and rejects a slug already taken by another account.
 */
export const UPDATE_PUBLIC_SITE = gql`
  mutation UpdatePublicSite($accountId: ID!, $input: PublicSiteInput!) {
    updatePublicSite(accountId: $accountId, input: $input) {
      id
      name
      publicSlug
      publicEnabled
      publicTitle
      publicTagline
      publicAccessProtected
    }
  }
`;

/**
 * Choose which attached image represents an item. Pass assetId: null to clear
 * the choice and fall back to role/position inference.
 */
export const SET_PRIMARY_MEDIA = gql`
  mutation SetPrimaryMedia($accountId: ID!, $ownerType: String!, $ownerId: ID!, $assetId: ID) {
    setPrimaryMedia(
      accountId: $accountId
      ownerType: $ownerType
      ownerId: $ownerId
      assetId: $assetId
    ) {
      assetId
      role
      position
      isPrimary
      url
    }
  }
`;

// ── Collect: capture ────────────────────────────────────────────────────────
// The browser capture client's write path. Keyed by SKU and idempotent, which
// is what makes the offline queue safe to replay: a retried save updates the
// same card rather than adding a second one.

export const SAVE_COLLECTION_ITEM = gql`
  mutation SaveCollectionItem($accountId: ID!, $input: CollectionItemInput!) {
    saveCollectionItem(accountId: $accountId, input: $input) {
      id
      sku
      title
      mediaUrl
      publishStatus
      customFields
      taxonomies
      updatedAt
    }
  }
`;

// ── Collect: capture devices ────────────────────────────────────────────────
// The pairing code comes back ONCE, for display as a QR. Only its hash is
// stored server-side, so it cannot be retrieved again — re-pair to get another.

export const CREATE_CAPTURE_DEVICE = gql`
  mutation CreateCaptureDevice($accountId: ID!, $name: String, $kind: String) {
    createCaptureDevice(accountId: $accountId, name: $name, kind: $kind) {
      pairingCode
      expiresAt
      device {
        id
        name
        kind
        connected
      }
    }
  }
`;

export const REPAIR_CAPTURE_DEVICE = gql`
  mutation RepairCaptureDevice($accountId: ID!, $deviceId: ID!) {
    repairCaptureDevice(accountId: $accountId, deviceId: $deviceId) {
      pairingCode
      expiresAt
    }
  }
`;

export const REVOKE_CAPTURE_DEVICE = gql`
  mutation RevokeCaptureDevice($accountId: ID!, $deviceId: ID!) {
    revokeCaptureDevice(accountId: $accountId, deviceId: $deviceId)
  }
`;

export const APPLY_COLLECTION_TEMPLATE = gql`
  mutation ApplyCollectionTemplate($accountId: ID!, $template: String!, $replace: Boolean) {
    applyCollectionTemplate(accountId: $accountId, template: $template, replace: $replace) {
      template
      fields
      dimensions
    }
  }
`;

// ── Collect: eBay ───────────────────────────────────────────────────────────

export const BEGIN_EBAY_AUTH = gql`
  mutation BeginEbayAuth($accountId: ID!, $returnTo: String) {
    beginEbayAuth(accountId: $accountId, returnTo: $returnTo) {
      url
      expiresInMinutes
    }
  }
`;

export const UPDATE_EBAY_SETTINGS = gql`
  mutation UpdateEbaySettings($accountId: ID!, $input: EbaySettingsInput!) {
    updateEbaySettings(accountId: $accountId, input: $input) {
      accountId
      marketplaceId
      defaultCategoryId
      paymentPolicyId
      returnPolicyId
      fulfillmentPolicyId
      titlePattern
      defaultCondition
      defaultQuantity
    }
  }
`;

export const DISCONNECT_EBAY = gql`
  mutation DisconnectEbay($accountId: ID!) {
    disconnectEbay(accountId: $accountId)
  }
`;

// ── TinyCMS: publishing checks + style documents ────────────────────────────

const TINYCMS_SETTINGS_FIELDS = `
  copyEditRequired
  styleGuide
  voiceNotes
  styleGuideUpdatedAt
  voiceNotesUpdatedAt
  canManage
`;

/** Admins only; the server refuses anyone else. */
export const UPDATE_TINYCMS_SETTINGS = gql`
  mutation UpdateTinycmsSettings($accountId: ID!, $input: TinyCmsSettingsInput!) {
    updateTinycmsSettings(accountId: $accountId, input: $input) {
      ${TINYCMS_SETTINGS_FIELDS}
    }
  }
`;

/** Replace the style guide or voice notes (kind: style_guide | voice_notes). */
export const UPDATE_TINYCMS_STYLE_DOCUMENT = gql`
  mutation UpdateTinycmsStyleDocument($accountId: ID!, $kind: TinyCmsStyleKind!, $body: String!) {
    updateTinycmsStyleDocument(accountId: $accountId, kind: $kind, body: $body) {
      ${TINYCMS_SETTINGS_FIELDS}
    }
  }
`;

// ── TinyCMS admin ───────────────────────────────────────────────────────────

const TINYCMS_ARTICLE_RESULT = `
  id
  slug
  title
  workingHash
  state
  content
  dataset
  publicPriority
  isActive
  updatedAt
`;

export const CREATE_TINYCMS_ARTICLE = gql`
  mutation CreateTinycmsArticle($accountId: ID!, $input: JSON!) {
    createTinycmsArticle(accountId: $accountId, input: $input) {
      ${TINYCMS_ARTICLE_RESULT}
    }
  }
`;

export const UPDATE_TINYCMS_ARTICLE = gql`
  mutation UpdateTinycmsArticle($accountId: ID!, $id: ID!, $input: JSON!) {
    updateTinycmsArticle(accountId: $accountId, id: $id, input: $input) {
      ${TINYCMS_ARTICLE_RESULT}
    }
  }
`;

export const RUN_TINYCMS_COPY_EDIT = gql`
  mutation RunTinycmsCopyEdit($accountId: ID!, $id: ID!) {
    runTinycmsCopyEdit(accountId: $accountId, id: $id)
  }
`;

export const RUN_TINYCMS_PAGE_COPY_EDIT = gql`
  mutation RunTinycmsPageCopyEdit($accountId: ID!, $id: ID!) {
    runTinycmsPageCopyEdit(accountId: $accountId, id: $id)
  }
`;

export const CREATE_TINYCMS_PREVIEW = gql`
  mutation CreateTinycmsPreview($accountId: ID!, $articleId: ID, $pageId: ID) {
    createTinycmsPreview(accountId: $accountId, articleId: $articleId, pageId: $pageId)
  }
`;

export const UPDATE_TINYCMS_SITE = gql`
  mutation UpdateTinycmsSite($accountId: ID!, $input: JSON!) {
    updateTinycmsSite(accountId: $accountId, input: $input)
  }
`;

/** Clicking Publish/Schedule is the approval: recorded with the member, now, and this hash. */
export const PUBLISH_TINYCMS_CONTENT = gql`
  mutation PublishTinycmsContent(
    $accountId: ID!
    $kind: TinyCmsKind!
    $id: ID
    $action: String!
    $contentHash: String!
    $scheduledFor: String
    $overrideReason: String
  ) {
    publishTinycmsContent(
      accountId: $accountId
      kind: $kind
      id: $id
      action: $action
      contentHash: $contentHash
      scheduledFor: $scheduledFor
      overrideReason: $overrideReason
    )
  }
`;

export const UNPUBLISH_TINYCMS_CONTENT = gql`
  mutation UnpublishTinycmsContent($accountId: ID!, $kind: TinyCmsKind!, $id: ID, $reason: String) {
    unpublishTinycmsContent(accountId: $accountId, kind: $kind, id: $id, reason: $reason)
  }
`;

// ── TinyCMS custom domains (admins only) ────────────────────────────────────

export const ADD_TINYCMS_DOMAIN = gql`
  mutation AddTinycmsDomain($accountId: ID!, $hostname: String!) {
    addTinycmsDomain(accountId: $accountId, hostname: $hostname)
  }
`;

export const CHECK_TINYCMS_DOMAIN = gql`
  mutation CheckTinycmsDomain($accountId: ID!, $id: ID!) {
    checkTinycmsDomain(accountId: $accountId, id: $id)
  }
`;

export const SET_PRIMARY_TINYCMS_DOMAIN = gql`
  mutation SetPrimaryTinycmsDomain($accountId: ID!, $id: ID) {
    setPrimaryTinycmsDomain(accountId: $accountId, id: $id)
  }
`;

export const REMOVE_TINYCMS_DOMAIN = gql`
  mutation RemoveTinycmsDomain($accountId: ID!, $id: ID!) {
    removeTinycmsDomain(accountId: $accountId, id: $id)
  }
`;

// ── TinyCMS analytics & custom code (admins only) ───────────────────────────

export const UPDATE_TINYCMS_INTEGRATIONS = gql`
  mutation UpdateTinycmsIntegrations($accountId: ID!, $input: JSON!, $note: String) {
    updateTinycmsIntegrations(accountId: $accountId, input: $input, note: $note)
  }
`;

export const RESTORE_TINYCMS_INTEGRATIONS = gql`
  mutation RestoreTinycmsIntegrations($accountId: ID!, $version: Int!) {
    restoreTinycmsIntegrations(accountId: $accountId, version: $version)
  }
`;

/** Turn an add-on the account holds off or back on (today: atomizer). */
export const SET_PRODUCT_ENABLED = gql`
  mutation SetProductEnabled($accountId: ID!, $product: String!, $enabled: Boolean!) {
    setProductEnabled(accountId: $accountId, product: $product, enabled: $enabled) {
      id
      products {
        product
        status
        plan
      }
    }
  }
`;
